import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { ReactNode } from "react";

import { cn } from "@/utils/shadcn-ui";

import { PermissionMetadataItem } from "../../../api";
import { BaseCheckbox } from "../../../components/ui-kit/fields/base-checkbox";
import { isVisibilityGroup, PermissionGroup } from "./permission-groups";
import {
  addPermissionWithImplied,
  getPermissionState,
  isDerivedState,
  type PermissionRules,
  removePermissionWithDependents,
} from "./permissions-utils";
import { Fields } from "./policy-fields";

/**
 * Permission bits grouped by the category the metadata endpoint reports, with
 * the routers first: checking a router auto-checks the bits it implies, which
 * then render checked and locked rather than as rows the admin has to tick one
 * by one. Forbidden bits render as excluded.
 *
 * Edits are pending - they land in `fields` and are written by the dialog's
 * Save.
 */
export const PermissionsPicker = observer(function PermissionsPicker(props: {
  fields: Fields;
  groups: PermissionGroup[];
  rules: PermissionRules;
}) {
  return (
    <>
      {props.groups
        .filter((group) => !isVisibilityGroup(group))
        .map((group) => (
          <PermissionSection key={group.name} title={group.name}>
            {group.items.map((item) => (
              <PermissionRow
                key={item.bit}
                item={item}
                fields={props.fields}
                rules={props.rules}
              />
            ))}
          </PermissionSection>
        ))}
    </>
  );
});

/**
 * The stack of sections the permissions dialog and the new-user wizard lay out,
 * scrolling between the dialog's title and its actions. The pickers render
 * their sections straight into it, so one rule separates every pair.
 *
 * `-mx-2 px-2`: a scroll box clips whatever leaves it, the checkboxes' focus
 * ring included - this widens the box into the dialog's padding while the
 * content stays where it was. It also keeps the scrollbar off the controls at
 * the rows' right end.
 */
export const PERMISSION_SECTIONS_CLASS =
  "-mx-2 min-h-0 flex-1 divide-y divide-border overflow-y-auto px-2";

/** A row of a section: a checkbox and its text, maybe controls after them. */
export const PERMISSION_ROW_CLASS =
  "flex flex-wrap items-center gap-x-2 gap-y-1 py-1 text-sm";

/**
 * One band of the permissions dialog: its title in a gutter on the left, its
 * rows in a grid beside it, so a category of a dozen bits takes a few lines
 * rather than a column of its own. Sized by its own width, not the viewport's,
 * as the wizard and a patient account's flags-only dialog are narrower.
 */
export function PermissionSection(props: {
  title: ReactNode;
  hint?: ReactNode;
  /** Rows wide enough to want two columns at most - those with controls
      after their text. */
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="@container py-4 first:pt-0 last:pb-0">
      {/* `items-baseline`: side by side, the title sits on the first row's
          text line, whatever height the rows are. */}
      <div className="grid items-baseline gap-x-8 gap-y-2 @2xl:grid-cols-[11rem_1fr]">
        <div>
          <h4 className="text-sm font-semibold">{props.title}</h4>
          {props.hint && (
            <p className="text-muted-foreground mt-0.5 text-xs">{props.hint}</p>
          )}
        </div>
        <div
          className={cn(
            "grid content-start gap-x-4 gap-y-0.5 @lg:grid-cols-2",
            !props.wide && "@4xl:grid-cols-3",
          )}
        >
          {props.children}
        </div>
      </div>
    </section>
  );
}

/**
 * One permission bit as a checkbox. `trailing` is where the visibility section
 * hangs the record picker that fills the scope in.
 */
export const PermissionRow = observer(function PermissionRow(props: {
  item: PermissionMetadataItem;
  fields: Fields;
  rules: PermissionRules;
  trailing?: ReactNode;
  className?: string;
}) {
  const { item, fields, rules } = props;
  const state = getPermissionState(
    item.bit,
    new Set(fields.permissions),
    rules,
  );
  const derived = isDerivedState(state);

  return (
    // `flex-wrap`: too narrow for the label and its picker side by side drops
    // the picker onto its own line, rather than squeezing the label until its
    // text runs under the button.
    <div className={cn(PERMISSION_ROW_CLASS, props.className)}>
      <BaseCheckbox
        checked={state === "checked" || state === "implied"}
        disabled={derived}
        aria-label={item.description}
        onCheckedChange={(checked) => {
          fields.permissions =
            checked === true
              ? addPermissionWithImplied(item.bit, fields.permissions, rules)
              : removePermissionWithDependents(
                  item.bit,
                  fields.permissions,
                  rules,
                );
        }}
        // Dim via opacity so the text stays legible on any background.
        // `self-baseline` makes the text, not the empty checkbox, the row's
        // baseline - the one `PermissionSection` sets its title on.
        label={
          <span className={cn("self-baseline", derived && "opacity-70")}>
            {item.description}
            {/* A space, not just a margin: it is where a long label breaks,
                so the tag wraps alone rather than taking the last word with
                it. */}
            {derived && " "}
            {state === "implied" && (
              <span className="text-2xs ml-0.5">
                <Trans>(implied)</Trans>
              </span>
            )}
            {state === "forbidden" && (
              <span className="text-2xs text-danger-foreground ml-0.5">
                <Trans>(excluded)</Trans>
              </span>
            )}
          </span>
        }
      />
      {/* A forbidden bit is a scope the policy cannot hold, so its record
          picker goes away with it - left live, it writes records into the draft
          that the Save would send for a scope that is not granted. `implied` is
          deliberately not covered: those bits are on, just locked, so their
          records still need picking. */}
      {props.trailing && state !== "forbidden" && (
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {props.trailing}
        </span>
      )}
    </div>
  );
});
